// One play's round of cards (render 04; a capsule on the gashapon): 4–5 cards, no timer. Right: the button squash-pops and the next
// card flicks in. Wrong: shake, the right one glows mint, the reading is revealed, "Got it".
// WI-08: a new card is met first (its reveal, heard, "Got it"), then asked; a right answer plays
// the clip and flashes the example word; a miss or a skip comes back later in the session.
// Speak Up's phrases are asked the same way: what does it mean? (owner, 23 Oct: no self-rating).
// Every answer is recorded as it happens, so closing early never loses anything (and needs no
// "are you sure?").
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import type { Pack } from '../../shared/pack/schema'
import { buzz } from '../audio/haptics'
import { canSay, say, stopSpeaking, contentLang, subscribeVoices } from '../audio/tts'
import { closeOverlay, replaceOverlay } from '../app/router'
import { useModal } from '../app/Sheet'
import { cardSetById, cardSetTitle, replaySound, soundBefore } from '../domain/arcade/session'
import type { Result } from '../domain/arcade/srs'
import { xpFor, type ChoiceItem, type Item } from '../domain/arcade/types'
import { springs } from '../motion/springs'
import { useMotionTier } from '../motion/tier'
import { t } from '../strings/en'
import { rightOn } from '../domain/arcade/plays'
import { useRewardMachine } from './machines'
import { advance, answerCard, finishLearn, useActive, useArcade, type ActiveSession } from './useArcade'

function SpeakerIcon({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#1B2A3A" strokeWidth="2.4" strokeLinejoin="round" aria-hidden>
      <path d="M4 9h4l5-4v14l-5-4H4z" fill="#fff" />
      <path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a7.5 7.5 0 0 1 0 11" />
    </svg>
  )
}

/** `text` can be heard on this phone (a recording or a voice), kept up to date as voices arrive. */
function useCanSay(text: string): boolean {
  return useSyncExternalStore(subscribeVoices, () => !text || canSay(text))
}

/** Big Japanese text shrinks to fit long phrases (never below the 32 px readable floor). */
function bigSize(text: string, max = 52): number {
  const n = [...text].length
  return Math.max(32, Math.min(max, Math.floor(300 / Math.max(n, 1))))
}

export function Session({ pack, personId, sessionKey }: { pack: Pack; personId: string; sessionKey: string }) {
  // Only this person's session under this overlay's key: a stale URL, or someone else's capsule
  // left in this tab, never shows (and answers never land under the wrong person).
  const stored = useActive((s) => s.active)
  const active = stored?.personId === personId && stored.key === sessionKey ? stored : null
  const { progress, events, settings } = useArcade(pack, personId)
  const still = useMotionTier() === 'still'
  const reward = useRewardMachine()

  // Nothing to play (e.g. reloaded after closing): back to this person's parked capsule, or leave.
  // A just-finished capsule hands over to the reward.
  // Set by ✕/Escape: the overlay is already closing, so ending a Learn session mustn't close it again.
  const leaving = useRef(false)
  useEffect(() => {
    if (active || leaving.current) return
    const s = useActive.getState()
    s.shelve(personId, sessionKey)
    if (s.resume(personId, sessionKey)) return
    if (s.done?.personId !== personId) closeOverlay()
  }, [active, personId, sessionKey])
  useEffect(() => () => stopSpeaking(), [])

  const item = active?.items[active.index]
  // ✕ and Escape: a Learn session is free, so closing ends it; a paid capsule stays resumable.
  const close = () => {
    leaving.current = true
    closeOverlay()
    if (active?.track) useActive.getState().set(null)
  }
  const ref = useRef<HTMLDivElement>(null)
  const headId = useId()
  useModal(ref, { onClose: close, active: !!item })
  if (!active || !item) return null
  const machine = cardSetById(active.machine)

  // Only Learn has rounds of cards now: a twist opens straight onto its reward (WI-13). A capsule
  // round left from before (an old tab) just ends.
  const next = async (a: ActiveSession) => {
    if (a.index + 1 >= a.items.length) {
      if (a.track) {
        await finishLearn(a, personId)
        replaceOverlay('learn-done')
      } else {
        useActive.getState().set(null)
        closeOverlay()
      }
    } else advance(a)
  }

  return (
    <div className={`session theme-${machine.theme.color}`} ref={ref} role="dialog" aria-modal="true" tabIndex={-1} aria-labelledby={headId}>
      <h2 className="sr-only" id={headId}>
        {cardSetTitle(machine.title, reward.words, pack)}
      </h2>
      <div className="session-head">
        <button className="x-btn press" aria-label={t.session.close} onClick={close}>
          ✕
        </button>
        <div className="segments" aria-label={`Card ${active.index + 1} of ${active.items.length}`}>
          {active.items.map((_, i) => (
            <i key={i} className={active.results[i] ?? (i === active.index ? 'current' : '')} />
          ))}
        </div>
        {active.track && settings ? (
          // Learn counts toward today's goal, not the session (mockup l02).
          <span className="pill" aria-label={t.learn.goalLabel}>
            {t.learn.goalPill(rightOn(events, personId, active.day), settings.learn.goal)}
          </span>
        ) : active.combo >= 2 ? (
          <motion.span key={active.combo} className="pill combo" initial={still ? false : { scale: 1.4 }} animate={{ scale: 1 }} transition={springs.bounce}>
            {t.session.combo(active.combo)}
          </motion.span>
        ) : (
          <span className="pill">{cardSetTitle(machine.title, reward.words, pack)}</span>
        )}
      </div>

      {/* A Learn session isn't a capsule (owner, 23 Oct: "I haven't opened a capsule to get to this
          screen"): no machine art. */}
      <div className="session-gap" aria-hidden />

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={active.index}
          initial={still ? { opacity: 0 } : { x: 60, opacity: 0 }}
          animate={still ? { opacity: 1 } : { x: 0, opacity: 1 }}
          exit={still ? { opacity: 0 } : { x: -120, opacity: 0, rotate: -6 }}
          transition={still ? { duration: 0.15 } : springs.snappy}
        >
          <Step item={item} still={still} onAnswer={(r, xp) => answerCard(active, progress, r, xp, personId)} onNext={(a) => void next(a)} />
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

/**
 * A card's header picture (owner, 23 Oct: the top of a card relates to its question): the pack's
 * situation picture for its context, or a script card's example-word picture. No `art`, no picture;
 * one that won't load (offline before it was cached) goes rather than showing a broken image.
 */
function CardArt({ src }: { src: string | undefined }) {
  const [failed, setFailed] = useState<string | null>(null)
  if (!src || failed === src) return null
  return <img className="card-art" src={src} alt="" decoding="async" onError={() => setFailed(src)} />
}

/** One card. Keyed by position, so its state starts fresh for every card. */
function Step({
  item,
  still,
  onAnswer,
  onNext,
}: {
  item: Item
  still: boolean
  onAnswer(r: Result, xp: number): Promise<ActiveSession>
  onNext(a: ActiveSession): void
}) {
  const [pick, setPick] = useState<number | null>(null)
  // The session once this card's answer is recorded (null until then).
  const [after, setAfter] = useState<ActiveSession | null>(null)

  // Sound on entry: only a sound that is the question (a phrase to understand). A card you read
  // (a kana, a word, a price) plays its sound after the answer, new or not (K2: heard first, it
  // gave the answer away).
  useEffect(() => {
    const clip = soundBefore(item)
    if (clip) say(clip)
  }, [item])

  async function choose(i: number) {
    if (pick !== null) return
    setPick(i)
    const right = i === item.answer
    buzz(right ? 'correct' : 'wrong')
    const a = await onAnswer(right ? 'right' : 'wrong', xpFor(item, right ? 'right' : 'wrong'))
    setAfter(a)
    // Right: hear it and see its example word for a moment, then on.
    if (item.reveal.say) say(item.reveal.say)
    if (right) setTimeout(() => onNext(a), still ? 600 : item.reveal.example ? 1500 : 900)
  }

  async function skip() {
    if (pick !== null) return
    setPick(-1)
    setAfter(await onAnswer('skip', 0))
    if (item.reveal.say) say(item.reveal.say)
  }

  const answered = pick !== null
  const replay = replaySound(item, answered) ?? ''
  const replayable = useCanSay(replay) && !!replay
  const showReveal = answered && pick !== item.answer
  const showFlash = answered && pick === item.answer

  return (
    <>
      <div className="session-body">
        <ChoiceCard item={item} pick={pick} still={still} onPick={(i) => void choose(i)} />
        {showReveal ? (
          // A miss or a skip: the answer, its reading and note; the card comes back later (WI-08).
          // It's scrolled into view above the footer (on a long card it would sit under it).
          <div
            className="panel reveal"
            role="status"
            ref={(el) => el?.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' })}
          >
            <div className="reveal-head">
              {item.reveal.big ? <b className="jp">{item.reveal.big}</b> : null}
              <b>{item.reveal.line}</b>
            </div>
            {item.reveal.note ? <span>{item.reveal.note}</span> : null}
          </div>
        ) : null}
        {showFlash && item.reveal.example ? (
          <motion.div className="panel reveal flash" role="status" initial={still ? false : { scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={springs.snappy}>
            <span>{item.reveal.example}</span>
          </motion.div>
        ) : null}
      </div>
      {createPortal(
        <div className="session-foot" data-modal-part>
          {/* A round speaker, plainly not an answer: hear it again. */}
          <button className="hear-again press" aria-label={replay && !replayable ? t.session.noVoice : t.session.hearAgain} onClick={() => say(replay)} disabled={!replayable}>
            <SpeakerIcon size={28} />
          </button>
          {showReveal ? (
            // Waits for the answer to be recorded: `after` carries the card put back (WI-08).
            <button className="btn y press" onClick={() => after && onNext(after)} disabled={!after}>
              {t.session.next}
            </button>
          ) : (
            <button className="btn w press" onClick={() => void skip()} disabled={answered}>
              {t.session.skip}
            </button>
          )}
        </div>,
        // Outside the animated card: position: fixed inside a transform would scroll with it.
        document.body,
      )}
    </>
  )
}
function ChoiceCard({ item, pick, still, onPick }: { item: ChoiceItem; pick: number | null; still: boolean; onPick(i: number): void }) {
  const heard = useCanSay(item.listen ?? '')
  return (
    <>
      <div className="panel card">
        <CardArt src={item.art} />
        {item.context ? <span className="mono">{item.context}</span> : null}
        {/* First time this card is asked: a small hint, and its sound plays (no separate meet card). */}
        {item.isNew ? <span className="tag yellow new-hint">{item.listen ? t.session.newHint : t.session.newHintRead}</span> : null}
        {item.big ? (
          <div className="big-jp" lang={contentLang()} style={{ fontSize: bigSize(item.big, 68) }}>
            {item.big}
          </div>
        ) : null}
        {item.text ? <p className="card-text">{item.text}</p> : null}
        {item.listen && heard ? (
          <button className="hear press theme-hear" aria-label={t.session.hearIt} onClick={() => say(item.listen ?? '')}>
            <SpeakerIcon size={44} />
          </button>
        ) : null}
        {/* WI-26: no recording and no voice for the language on this phone: read it instead. */}
        {item.listen && !heard ? (
          <div className="no-voice" role="note">
            <span className="tag blue">{t.session.noVoice}</span>
            <div className="no-voice-text" lang={contentLang()} style={{ fontSize: bigSize(item.listen, 44) }}>
              {item.listen}
            </div>
            <small className="muted">{t.session.noVoiceSub}</small>
          </div>
        ) : null}
        {item.question ? <span className="question">{item.question}</span> : null}
        {item.listen && heard ? <small className="muted">{t.session.tapToHear}</small> : null}
      </div>
      <div className={`answers${item.glyphs ? ' glyphs' : item.options.some((o) => o.length > 14) ? ' long' : ''}`} lang={item.glyphs ? contentLang() : undefined}>
        {item.options.map((o, i) => {
          const isPick = pick === i
          const isRight = pick !== null && i === item.answer
          const cls = isRight ? ' ok' : isPick ? ' bad' : ''
          return (
            <motion.button
              key={o}
              className={`btn w answer${cls}`}
              onClick={() => onPick(i)}
              disabled={pick !== null && !isRight && !isPick}
              animate={
                still || !isPick
                  ? undefined
                  : isRight
                    ? { scale: [1, 1.08, 0.96, 1] }
                    : { x: [0, -6, 6, -6, 6, -6, 0] }
              }
              transition={isRight ? springs.bounce : { duration: 0.3 }}
              whileTap={pick === null && !still ? { scale: 0.96 } : undefined}
            >
              {o}
              {isRight ? ' ✓' : ''}
            </motion.button>
          )
        })}
      </div>
    </>
  )
}
